import { AppId, Button, ButtonType, cssX, ProductLogo, Tooltip, usePlatformContext } from "@datassential/platform-ui-lib";
import cn from "classnames";
import { sanitizeUrl } from "@helpers";
import { IconPackages } from "@icons";
import { MacExportFormat } from "@apiHooks/useMacExport";
import "./MacDetailsActions.scss";

interface MacDetailsActionsProps {
    sendToKeywords: string[];
    canSendTo: boolean;
    isDownloading: boolean;
    onAddToPackage: () => void;
    onExport: (format: MacExportFormat) => void;
}

const SEND_TO_TOOLTIP_ID = 'mac-details-send-to-tooltip';
export const SEND_TO_DISABLED_HINT = 'Select at least one stage on the chart to send its keywords to Menu Trends';

const EXPORT_OPTIONS: { label: string; format: MacExportFormat; testId: string }[] = [
    { label: 'PNG', format: 'png', testId: 'buttonMacDetailsDownloadImage' },
    { label: 'PPT', format: 'pptx', testId: 'buttonMacDetailsDownloadPpt' },
];

function MacDetailsActions({ sendToKeywords, canSendTo, isDownloading, onAddToPackage, onExport }: MacDetailsActionsProps) {
    const { userInfo: { products } } = usePlatformContext();
    const menuTrendsUrl = products[AppId.MenuTrends]?.url ?? '';
    const isSendToDisabled = sendToKeywords.length === 0;
    const sendToLink = sanitizeUrl(
        `${menuTrendsUrl}${process.env.REACT_APP_MT_BYO_LINK}${encodeURIComponent(sendToKeywords.join(','))}`,
    );

    return (
        <div className="MacDetailsActions">
            {canSendTo && (
                // The disabled button ignores pointer events, so the wrapper anchors the hint tooltip.
                <span
                    className="MacDetailsActions__send-to"
                    data-tooltip-id={isSendToDisabled ? SEND_TO_TOOLTIP_ID : undefined}
                >
                    <Button
                        type={ButtonType.Anchor}
                        link={sendToLink}
                        isDisabled={isSendToDisabled}
                        className={cn('MacDetailsActions__button', { 'is-inactive': isSendToDisabled })}
                        testId="buttonMacDetailsGoToMenuTrends"
                    >
                        Send To
                        <ProductLogo type="regular" appId={AppId.MenuTrends} className="MacDetailsActions__logo"/>
                    </Button>
                </span>
            )}
            {canSendTo && isSendToDisabled && (
                <Tooltip id={SEND_TO_TOOLTIP_ID} place="top">
                    {SEND_TO_DISABLED_HINT}
                </Tooltip>
            )}
            <Button
                onClick={onAddToPackage}
                className="MacDetailsActions__button"
                testId="buttonMacDetailsAddToPackage"
            >
                Package
                <IconPackages className="MacDetailsActions__icon"/>
            </Button>
            <Button
                type={ButtonType.HoverOptions}
                isDisabled={isDownloading}
                className={cn('MacDetailsActions__button', { 'is-loading': isDownloading })}
                testId="buttonMacDetailsDownload"
                hoverContent={EXPORT_OPTIONS.map(({ label, format, testId }) => (
                    <button
                        key={format}
                        type="button"
                        className={cn(cssX('Button__option'), 'MacDetailsActions__option')}
                        onClick={() => onExport(format)}
                        data-testid={testId}
                    >
                        {label}
                    </button>
                ))}
            >
                {isDownloading ? 'loading...' : 'Download'}
            </Button>
        </div>
    );
}

export default MacDetailsActions;
